import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

const CRYPTOLIB_WINDOWS =
  "https://esante.gouv.fr/services/espace-cps/telechargements-libres/cryptolib-cps-windows";
const CRYPTOLIB_MACOS =
  "https://esante.gouv.fr/services/espace-cps/telechargements-libres/cryptolib-cps-mac-os-x";

const PREREQUIS = [
  {
    t: "Un lecteur de cartes CPS installé et configuré",
    d: "Les lecteurs PS/SC comme les lecteurs GALSS conviennent. Un lecteur de carte Vitale n'est pas obligatoire : il n'est utile que si vous lisez aussi des cartes Vitale.",
  },
  {
    t: "La Cryptolib CPS, en version 5 au minimum",
    d: "C'est la bibliothèque officielle qui permet à votre poste de lire une carte CPS. Elle est distribuée gratuitement par l'Agence du numérique en santé. Si vous ne savez pas quelle version est installée, réinstallez la dernière : elle remplace la précédente.",
  },
];

const ETAPES = [
  {
    t: "Ouvrez la fenêtre de supervision",
    d: "Cliquez une fois sur l'icône du connecteur, dans la zone des icônes près de l'horloge. Si vous ne la voyez pas, cliquez d'abord sur la flèche à gauche de l'horloge pour déplier les icônes masquées.",
  },
  {
    t: "Regardez l'état du service",
    d: "La fenêtre indique si le service est démarré ou arrêté. S'il est arrêté, cliquez sur Démarrer. S'il est déjà démarré mais que rien ne fonctionne, cliquez sur Arrêter, puis sur Démarrer.",
  },
  {
    t: "Revenez sur votre application et rechargez",
    d: "Retournez sur l'onglet où vous étiez et rechargez la page avec Ctrl+R, ou F5. Tant qu'elle n'a pas été rechargée, elle garde en mémoire l'échec de la tentative précédente.",
  },
];

const RESTE = [
  {
    t: "L'icône du connecteur n'existe nulle part",
    d: "Le connecteur n'est pas installé, ou il ne s'est pas lancé avec votre session. Redémarrez votre poste : il démarre automatiquement. Si l'icône n'apparaît toujours pas, reprenez l'installation.",
  },
  {
    t: "Le lecteur n'est pas reconnu",
    d: "Débranchez-le, rebranchez-le, puis rouvrez votre navigateur. Si la carte n'est toujours pas lue, c'est le pilote du lecteur, ou la Cryptolib CPS, qu'il faut installer ou mettre à jour.",
  },
  {
    t: "Votre antivirus ou votre pare-feu intervient",
    d: "Certains filtrent les échanges entre le navigateur et les programmes locaux. Il faut alors autoriser le connecteur dans leur liste d'exceptions.",
  },
  {
    t: "Le poste est géré par un service informatique",
    d: "L'installation comme le démarrage du service peuvent demander une autorisation. Transmettez-leur cette page : il s'agit d'un programme local, sans ouverture de port entrant.",
  },
];

export default function DownloadCheck() {
  return (
    <div className="vrf">
      <SEO
        title="Vérifier que le connecteur fonctionne · MetaConnect"
        description="Prérequis du poste, contrôle du service dans la fenêtre de supervision, autorisation du réseau local sur Chrome : que faire si la carte n'est pas détectée."
        canonical="/download/verification"
      />
      <style>{CSS}</style>

      <section className="vrf-hero">
        <div className="vrf-wrap">
          <span className="vrf-pill">Connecteur poste de travail</span>
          <h1>Vérifier que le connecteur fonctionne</h1>
          <p className="vrf-lede">
            Le connecteur est installé, mais votre carte n'est toujours pas détectée ? Avant
            d'appeler qui que ce soit, trois minutes suffisent pour savoir d'où vient le blocage :
            un prérequis manquant sur le poste, un service à l'arrêt, ou une autorisation du
            navigateur.
          </p>
          <p className="vrf-back">
            Pas encore installé ? <Link to="/download">Commencez par le télécharger</Link>.
          </p>
        </div>
      </section>

      <section className="vrf-sec vrf-alt">
        <div className="vrf-wrap">
          <div className="vrf-head">
            <h2>Ce que le poste doit avoir</h2>
            <p>Deux prérequis, indépendants du connecteur lui-même.</p>
          </div>
          <div className="vrf-grid">
            {PREREQUIS.map((p) => (
              <div className="vrf-card" key={p.t}>
                <h3>{p.t}</h3>
                <p>{p.d}</p>
              </div>
            ))}
          </div>
          <div className="vrf-links">
            <span>Télécharger la Cryptolib CPS sur le site de l'Agence du numérique en santé :</span>
            <a href={CRYPTOLIB_WINDOWS} target="_blank" rel="noopener noreferrer">
              Version Windows <em>&rsaquo;</em>
            </a>
            <a href={CRYPTOLIB_MACOS} target="_blank" rel="noopener noreferrer">
              Version macOS <em>&rsaquo;</em>
            </a>
          </div>
        </div>
      </section>

      <section className="vrf-sec">
        <div className="vrf-wrap">
          <div className="vrf-head">
            <h2>Contrôler le service en trois gestes</h2>
            <p>
              Le connecteur tourne en arrière-plan et se pilote depuis une petite fenêtre de
              supervision. Procédure décrite pour Windows.
            </p>
          </div>
          <ol className="vrf-steps">
            {ETAPES.map((e, i) => (
              <li key={e.t}>
                <span className="vrf-num">{i + 1}</span>
                <div>
                  <h3>{e.t}</h3>
                  <p>{e.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="vrf-sec vrf-alt">
        <div className="vrf-wrap">
          <div className="vrf-note">
            <span className="vrf-tag">Chrome 142 et versions suivantes</span>
            <h2>Autorisez l'accès au réseau local</h2>
            <p>
              Depuis sa version 142, Chrome demande une autorisation explicite avant qu'une page web
              puisse dialoguer avec un programme installé sur votre poste. Sans elle, le connecteur a
              beau tourner : la page ne le voit pas.
            </p>
            <p>
              Cliquez sur l'icône située à gauche de l'adresse du site, dans la barre d'adresse,
              ouvrez les paramètres du site, puis autorisez l'accès au réseau local. Rechargez
              ensuite la page.
            </p>
          </div>
        </div>
      </section>

      <section className="vrf-sec">
        <div className="vrf-wrap">
          <div className="vrf-head">
            <h2>Si rien n'y fait</h2>
          </div>
          <div className="vrf-grid vrf-grid2">
            {RESTE.map((r) => (
              <div className="vrf-card" key={r.t}>
                <h3>{r.t}</h3>
                <p>{r.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="vrf-cta">
        <div className="vrf-wrap">
          <h2>On regarde ensemble ?</h2>
          <p>
            Nous prenons la main sur votre poste et nous réglons le problème avec vous. Comptez dix
            minutes.
          </p>
          <Link className="vrf-cbtn" to="/support-distant">
            Demander une assistance à distance
          </Link>
        </div>
      </section>
    </div>
  );
}

const CSS = `
.vrf{
  --ground:#F5F5F5;--surface:#fff;--line:#E6E8EC;--text:#0F172A;--muted:#5A6478;
  --blue:#0080FF;--bsoft:#EAF4FF;--bline:#CFE4FF;
  background:var(--ground);color:var(--text);
  font-family:"Instrument Sans",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;
  -webkit-font-smoothing:antialiased;
}
.vrf *{box-sizing:border-box}
.vrf h1,.vrf h2,.vrf h3{margin:0;color:var(--text);font-weight:600;text-wrap:balance}
.vrf h1{font-size:clamp(29px,3.6vw,40px);line-height:1.14;letter-spacing:-.02em}
.vrf p{margin:0;color:var(--muted);line-height:1.6}
.vrf ul,.vrf ol{list-style:none;margin:0;padding:0}
.vrf-wrap{max-width:1000px;margin:0 auto;padding:0 28px}

.vrf-pill{display:inline-flex;border-radius:999px;background:#E7ECF3;color:#45526B;
  font-size:11.5px;font-weight:600;letter-spacing:.11em;text-transform:uppercase;padding:7px 15px}
.vrf-hero{padding:82px 0 46px;text-align:center}
.vrf-hero h1{margin:16px auto 0;max-width:20ch}
.vrf .vrf-lede{max-width:64ch;margin:16px auto 0;font-size:16.5px}
.vrf .vrf-back{margin:20px auto 0;font-size:14.4px}
.vrf-back a{color:#0B62C4;font-weight:600;text-decoration:none}
.vrf-back a:hover{text-decoration:underline}

.vrf-sec{padding:56px 0}
.vrf-alt{background:#fff;border-top:1px solid var(--line);border-bottom:1px solid var(--line)}
.vrf-head{text-align:center;max-width:64ch;margin:0 auto}
.vrf-head h2{font-size:clamp(22px,2.6vw,30px);line-height:1.2;letter-spacing:-.018em}
.vrf-head p{margin-top:12px;font-size:15.5px}

.vrf-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:14px;margin-top:30px}
.vrf-grid2{grid-template-columns:repeat(2,1fr)}
.vrf-card{background:var(--ground);border:1px solid var(--line);border-radius:14px;padding:22px 22px 24px}
.vrf-card h3{font-size:16px;line-height:1.3;margin-bottom:9px}
.vrf-card p{font-size:13.8px;line-height:1.62}

.vrf-links{display:flex;align-items:center;justify-content:center;gap:12px;flex-wrap:wrap;
  margin-top:22px;background:var(--bsoft);border:1px solid var(--bline);border-radius:14px;
  padding:18px 22px}
.vrf-links span{color:#3D4759;font-size:14px}
.vrf-links a{display:inline-flex;align-items:center;gap:8px;background:#fff;border:1px solid var(--bline);
  border-radius:999px;padding:9px 18px;color:#0B62C4;font-weight:600;font-size:14px;
  text-decoration:none;transition:background .18s}
.vrf-links a:hover{background:#DBEBFF}
.vrf-links em{font-style:normal;font-size:17px;line-height:1}

.vrf-steps{display:grid;gap:12px;margin-top:30px}
.vrf-steps li{display:flex;gap:16px;align-items:flex-start;background:#fff;border:1px solid var(--line);
  border-radius:14px;padding:20px 22px}
.vrf-num{flex:0 0 30px;width:30px;height:30px;border-radius:50%;background:var(--bsoft);
  border:1px solid var(--bline);color:#0B62C4;font-weight:600;font-size:14px;
  display:grid;place-items:center;margin-top:1px}
.vrf-steps h3{font-size:16px;line-height:1.3;margin-bottom:7px}
.vrf-steps p{font-size:13.9px;line-height:1.62}

.vrf-note{background:var(--ground);border:1px solid var(--line);border-radius:18px;
  padding:30px 32px;max-width:760px;margin:0 auto}
.vrf-tag{display:inline-flex;border-radius:999px;background:#FFF2D8;color:#8A5B00;
  font-size:11.5px;font-weight:600;letter-spacing:.09em;text-transform:uppercase;padding:6px 13px}
.vrf-note h2{font-size:clamp(20px,2.3vw,26px);line-height:1.22;margin:14px 0 12px;letter-spacing:-.015em}
.vrf-note p{font-size:14.6px}
.vrf-note p + p{margin-top:12px}

.vrf-cta{background:var(--blue);padding:56px 0;text-align:center}
.vrf-cta h2{color:#fff;font-size:clamp(22px,2.6vw,30px);margin-bottom:12px}
.vrf-cta p{color:rgba(255,255,255,.9);font-size:15.5px;max-width:60ch;margin:0 auto 24px}
.vrf-cbtn{display:inline-flex;align-items:center;justify-content:center;border-radius:999px;
  background:#fff;color:var(--blue);font-weight:600;font-size:15px;text-decoration:none;
  padding:13px 28px}
.vrf-cbtn:hover{background:#EAF4FF;color:var(--blue)}

@media (max-width:880px){
  .vrf-grid,.vrf-grid2{grid-template-columns:1fr}
  .vrf-hero{padding:56px 0 36px}
  .vrf-note{padding:24px 22px}
}
.vrf a:focus-visible{outline:2px solid var(--blue);outline-offset:3px;border-radius:6px}
`;
